import React from 'react';
import { Link } from 'react-router-dom';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-orange-100">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <span className="text-2xl">🎨</span>
              <span className="text-xl font-bold text-gray-800">
                Kitchen<span className="text-orange-500">Canvas</span>
              </span>
            </Link>
            <Link to="/" className="text-sm text-gray-500 hover:text-orange-500 transition-colors flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-10 text-center">
          <span className="text-5xl block mb-4">📜</span>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-gray-500">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 space-y-8 text-gray-700">
          
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex items-start gap-4">
            <span className="text-2xl">👋</span>
            <div>
              <h3 className="font-bold text-amber-800 mb-1">Welcome to Kitchen Canvas!</h3>
              <p className="text-amber-700 text-sm">By using our app, you agree to these terms. They're here to make sure everyone has a great cooking experience.</p>
            </div>
          </div>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-8 h-8 bg-orange-500 text-white rounded-lg flex items-center justify-center text-sm font-bold">1</span>
              Acceptance of Terms
            </h2>
            <p className="leading-relaxed">
              By downloading, installing, or using Kitchen Canvas ("the App"), you agree to be bound by these Terms of Service. If you don't agree with any part of these terms, please don't use the App.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-8 h-8 bg-orange-500 text-white rounded-lg flex items-center justify-center text-sm font-bold">2</span>
              License to Use
            </h2>
            <p className="leading-relaxed mb-4">
              We grant you a free, non-exclusive, non-transferable license to use Kitchen Canvas for personal or household purposes. This means you can:
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-xl text-green-800">
                <span>✅</span> Install on your personal devices
              </div>
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-xl text-green-800">
                <span>✅</span> Use for home cooking
              </div>
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-xl text-green-800">
                <span>✅</span> Save unlimited recipes
              </div>
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-xl text-green-800">
                <span>✅</span> Use all features for free
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-8 h-8 bg-orange-500 text-white rounded-lg flex items-center justify-center text-sm font-bold">3</span>
              Things You Shouldn't Do
            </h2>
            <p className="leading-relaxed mb-4">
              Please don't:
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl text-red-800">
                <span>❌</span> Reverse engineer, modify, or tamper with the App
              </div>
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl text-red-800">
                <span>❌</span> Sell, rent, or redistribute the App
              </div>
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl text-red-800">
                <span>❌</span> Use the App for any illegal activities
              </div>
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl text-red-800">
                <span>❌</span> Remove any copyright or ownership notices
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-8 h-8 bg-orange-500 text-white rounded-lg flex items-center justify-center text-sm font-bold">4</span>
              AI-Generated Recipes
            </h2>
            <p className="leading-relaxed">
              Kitchen Canvas uses AI to generate recipe suggestions. While we strive for accuracy, please note that AI-generated recipes are suggestions only. Always use your judgment when cooking, especially regarding food allergies, dietary restrictions, and food safety practices. We are not responsible for the results of cooking recipes generated by the App.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-8 h-8 bg-orange-500 text-white rounded-lg flex items-center justify-center text-sm font-bold">5</span>
              Intellectual Property
            </h2>
            <p className="leading-relaxed">
              Kitchen Canvas, including its design, features, and code, is owned by us and protected by intellectual property laws. The recipes you create are yours to keep, but the underlying App technology remains our property.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-8 h-8 bg-orange-500 text-white rounded-lg flex items-center justify-center text-sm font-bold">6</span>
              Disclaimer of Warranties
            </h2>
            <div className="bg-gray-50 rounded-xl p-5 text-sm">
              <p className="leading-relaxed">
                THE APP IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DON'T GUARANTEE THAT THE APP WILL BE ERROR-FREE, UNINTERRUPTED, OR MEET YOUR SPECIFIC REQUIREMENTS. USE AT YOUR OWN RISK.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-8 h-8 bg-orange-500 text-white rounded-lg flex items-center justify-center text-sm font-bold">7</span>
              Limitation of Liability
            </h2>
            <div className="bg-gray-50 rounded-xl p-5 text-sm">
              <p className="leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE APP.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-8 h-8 bg-orange-500 text-white rounded-lg flex items-center justify-center text-sm font-bold">8</span>
              Changes to These Terms
            </h2>
            <p className="leading-relaxed">
              We may update these Terms from time to time. We'll post the updated version here with a new date. By continuing to use the App after changes are made, you accept the new terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-8 h-8 bg-orange-500 text-white rounded-lg flex items-center justify-center text-sm font-bold">9</span>
              Contact Us
            </h2>
            <p className="leading-relaxed">
              Have questions about these terms? We're here to help! Contact us at{' '}
              <a href="mailto:support@kitchen-canvas.com" className="text-orange-500 hover:underline font-medium">
                support@kitchen-canvas.com
              </a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-orange-100 mt-12">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xl">🎨</span>
            <span className="font-bold text-gray-800">KitchenCanvas</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link to="/privacy" className="hover:text-orange-500 transition-colors">Privacy</Link>
            <Link to="/terms" className="text-orange-500 font-medium">Terms</Link>
          </div>
          <div className="text-sm text-gray-400">© {new Date().getFullYear()} Kitchen Canvas</div>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
